import cx from 'classnames';
import { IconButton } from '@components/iconButton/IconButton';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import typography from '~styles/typography.scss';
import type { Coupon } from './apiHelpers';
import styles from './VipProgramSection.scss';

export interface CouponsTableProps {
  coupons: Coupon[];
  onDeleteCoupon: (coupon: Coupon) => void;
  onEditCoupon: (coupon: Coupon) => void;
}

export const CouponsTable = ({
  coupons,
  onDeleteCoupon,
  onEditCoupon,
}: CouponsTableProps) => {
  const handleCopyCouponCode = async (coupon: Coupon) => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      successToast({ message: `${coupon.code} copied to clipboard` });
    } catch (error) {
      errorToast({ message: 'Failed to copy coupon code. Please try again.' });
      reportAppError(error);
    }
  };

  return (
    <div className={styles.couponsTableWrapper}>
      <table className={styles.couponsTable}>
        <thead>
          <tr>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.indexCell,
              )}
            >
              #
            </th>
            <th
              className={cx(typography.t2, styles.tableHeader, styles.nameCell)}
            >
              Name
            </th>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.descriptionCell,
              )}
            >
              Description
            </th>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.statusCell,
              )}
            >
              Status
            </th>
            <th
              className={cx(
                typography.t2,
                styles.tableHeader,
                styles.actionsCell,
              )}
              aria-label="Actions"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {coupons.map((coupon, index) => (
            <tr
              key={coupon.id}
              className={styles.clickableCouponRow}
              onClick={() => {
                onEditCoupon(coupon);
              }}
            >
              <td className={cx(typography.c2, styles.indexCell)}>
                {index + 1}
              </td>
              <td className={cx(typography.c2, styles.nameCell)}>
                {coupon.name}
              </td>
              <td className={cx(typography.c2, styles.descriptionCell)}>
                {coupon.description}
              </td>
              <td className={styles.statusCell}>
                <span
                  className={cx(
                    typography.t3,
                    styles.pill,
                    styles[coupon.status],
                  )}
                >
                  {coupon.status}
                </span>
              </td>
              <td className={styles.actionsCell}>
                <div className={styles.actions}>
                  <IconButton
                    ariaLabel={`Copy coupon code`}
                    className={styles.actionButton}
                    iconName="copy"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleCopyCouponCode(coupon);
                    }}
                  />
                  <IconButton
                    ariaLabel={`Edit ${coupon.name}`}
                    className={styles.actionButton}
                    iconName="pencil"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditCoupon(coupon);
                    }}
                  />
                  <IconButton
                    ariaLabel={`Delete ${coupon.name}`}
                    className={styles.actionButton}
                    iconName="trash"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCoupon(coupon);
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
